/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal, DeletePromptModalProps } from 'src/core/components/DeletePromptModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

export type IntentDeleteModalProps = DeletePromptModalProps<IntentDeleteModalDto>;

export type IntentDeleteModalDto = {
    /**
     * Whether to keep the intent's utterances in the none
     * intent or delete them with the intent.
     */
    keepUtterancesAndPatterns: boolean;
};

const messages = defineMessages({
    modalTitle: {
        id: 'intents.intentDeleteModal.modalTitle',
        defaultMessage: 'Delete intent(s)?'
    },
    modalBody: {
        id: 'intents.intentDeleteModal.modalBody',
        defaultMessage: 'Are you sure you want to delete the selected intent(s)?'
    },
    keepUtterancesMessage: {
        id: 'intents.intentDeleteModal.keepUtterancesMessage',
        defaultMessage: 'Keep this intent\'s utterances and patterns in the None intent'
    }
});

/**
 * Represents a modal for intent deletion.
 *
 * @param props The input properties for the modal.
 */
export const IntentDeleteModal = withModalMounter((props: IntentDeleteModalProps) => {
    const { onDelete } = props;
    const { localize } = useLocalization();
    const [checked, setChecked] = React.useState<boolean>(false);

    return (
        <DeletePromptModal
            {...props}
            dialogContentProps={{ title: localize(messages.modalTitle), subText: localize(messages.modalBody) }}
            onDelete={() => onDelete({ keepUtterancesAndPatterns: checked })}>
            <Checkbox
                label={localize(messages.keepUtterancesMessage)}
                ariaLabel={localize(messages.keepUtterancesMessage)}
                checked={checked}
                onChange={(_, val) => setChecked(val)}
            />
        </DeletePromptModal>
    );
});
